<template>
  <div class="dashboard-view">
    <page-title pageTitle="Dashboard" title="Dashboard" />

    <div class="row g-4 mb-4">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        class="col-xl-3 col-md-6"
      >
        <div class="card h-100 shadow-sm summary-card">
          <div class="card-body">
            <p class="text-muted text-uppercase fw-semibold small mb-2">
              {{ card.label }}
            </p>
            <h3 class="mb-1">{{ card.value }}</h3>
            <p v-if="card.helper" class="text-muted small mb-0">
              {{ card.helper }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <div class="col-xl-8">
        <dashboard-chart-card
          title="Sales trend"
          :subtitle="`Last ${salesTrendDays} days`"
          type="combo"
          :chart-data="salesTrendChartData"
          :loading="dailySalesReport.loading"
          empty-text="Sales activity will appear here once orders start coming in."
        />
      </div>
      <div class="col-xl-4">
        <dashboard-chart-card
          title="Sales by category"
          subtitle="Current month"
          type="doughnut"
          :chart-data="categoryChartData"
          :loading="salesByCategoryReport.loading"
          empty-text="Add product sales to see category performance."
        >
          <template #action>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="refreshCategoryReport"
            >
              <i class="las la-sync-alt me-1"></i>
              Refresh
            </button>
          </template>
        </dashboard-chart-card>
      </div>
    </div>

    <div class="row g-4 mt-0">
      <div class="col-xl-6">
        <dashboard-chart-card
          title="Top selling products"
          subtitle="By quantity sold"
          type="bar"
          :chart-data="topProductsChartData"
          :loading="topProductsReport.loading"
          empty-text="You'll see your best performers here after recording sales."
        />
      </div>
      <div class="col-xl-6">
        <div class="card h-100 shadow-sm">
          <div
            class="card-header border-0 pb-0 d-flex justify-content-between align-items-center"
          >
            <div>
              <h4 class="card-title mb-1">Recent orders</h4>
              <p class="text-muted mb-0">Latest five invoices</p>
            </div>
            <router-link class="btn btn-sm btn-outline-primary" to="/invoices">
              View all
            </router-link>
          </div>
          <div class="card-body pt-0">
            <div v-if="invoicesLoading" class="py-5 text-center text-muted">
              <div class="spinner-border text-primary mb-2" role="status"></div>
              <div>Loading orders…</div>
            </div>
            <div
              v-else-if="recentInvoices.length === 0"
              class="py-5 text-center text-muted"
            >
              No invoices yet. Create one to see it here.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-nowrap align-middle mb-0">
                <thead>
                  <tr class="text-muted text-uppercase small">
                    <th scope="col">Invoice</th>
                    <th scope="col">Customer</th>
                    <th scope="col" class="text-end">Total</th>
                    <th scope="col" class="text-center">Status</th>
                    <th scope="col" class="text-end">Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invoice in recentInvoices" :key="invoice.id">
                    <td>
                      <router-link
                        :to="`/invoices/${invoice.id}`"
                        class="fw-semibold"
                      >
                        {{ invoice.invoiceNo || `#${invoice.id}` }}
                      </router-link>
                    </td>
                    <td>
                      <div class="text-truncate" style="max-width: 180px">
                        {{ invoice.customer || "Walk-in customer" }}
                      </div>
                    </td>
                    <td class="text-end fw-semibold">
                      {{ formatCurrency(invoice.total) }}
                    </td>
                    <td class="text-center">
                      <span :class="statusBadge(invoice.status)">
                        {{ formatStatus(invoice.status) }}
                      </span>
                    </td>
                    <td class="text-end text-muted">
                      {{ formatDate(invoice.date) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PageTitleComponent from "@/components/shared/PageTitle";
import DashboardChartCard from "@/components/dashboard/DashboardChartCard";

export default {
  name: "DashboardView",
  components: {
    PageTitle: PageTitleComponent,
    DashboardChartCard,
  },
  data() {
    return {
      chartPalette: [
        "#4f46e5",
        "#0ea5e9",
        "#f97316",
        "#22c55e",
        "#ef4444",
        "#8b5cf6",
        "#14b8a6",
      ],
      topProductsChartData: [],
      categoryChartData: [],
      salesTrendChartData: [],
      salesTrendDays: 14,
      salesSummary: {
        totalRevenue: 0,
        totalOrders: 0,
      },
    };
  },
  computed: {
    ...mapGetters([
      "topProductsReport",
      "statusOverviewReport",
      "salesByCategoryReport",
      "dailySalesReport",
      "allInvoices",
      "invoicesLoading",
    ]),
    topProductsData() {
      const products =
        (this.topProductsReport && this.topProductsReport.topSellingAnalysis) ||
        [];
      return products.slice(0, 6);
    },
    categoryBreakdownData() {
      return (
        (this.salesByCategoryReport &&
          this.salesByCategoryReport.categoryBreakdown) ||
        []
      );
    },
    dailySalesData() {
      return (
        (this.dailySalesReport && this.dailySalesReport.salesTimeline) || []
      );
    },
    statusOverviewData() {
      const analysis =
        (this.statusOverviewReport &&
          this.statusOverviewReport.statusAnalysis) ||
        [];
      if (analysis.length > 0) {
        return analysis[0];
      }
      return {
        quantitySold: 0,
        invoicesPaid: 0,
        activeCustomers: 0,
      };
    },
    recentInvoices() {
      const invoices = this.allInvoices || [];
      return invoices.slice(0, 5);
    },
    summaryCards() {
      return [
        {
          key: "revenue",
          label: `Revenue (last ${this.salesTrendDays} days)`,
          value: this.formatCurrency(this.salesSummary.totalRevenue),
          helper: `${this.formatNumber(
            this.salesSummary.totalOrders
          )} orders processed`,
        },
        {
          key: "units",
          label: "Units sold (current month)",
          value: this.formatNumber(this.statusOverviewData.quantitySold || 0),
        },
        {
          key: "invoices",
          label: "Invoices paid (current month)",
          value: this.formatNumber(this.statusOverviewData.invoicesPaid || 0),
        },
        {
          key: "customers",
          label: "Active customers (current month)",
          value: this.formatNumber(
            this.statusOverviewData.activeCustomers || 0
          ),
        },
      ];
    },
  },
  watch: {
    topProductsData: {
      handler(data) {
        this.updateTopProductsChart(data);
      },
      deep: true,
      immediate: true,
    },
    categoryBreakdownData: {
      handler(data) {
        this.updateCategoryChart(data);
      },
      deep: true,
      immediate: true,
    },
    dailySalesData: {
      handler(data) {
        this.updateSalesTrendChart(data);
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.fetchDashboardData();
  },
  methods: {
    async fetchDashboardData() {
      await Promise.allSettled([
        this.$store.dispatch("getTopProductReport", {
          currentPage: 1,
          resultsPerPage: 6,
          sortBy: "quantity",
        }),
        this.$store.dispatch("getStatusOverviewReport", { trend: "month" }),
        this.$store.dispatch("getSalesByCategoryReport", { trend: "month" }),
        this.$store.dispatch("getDailySalesReport", {
          days: this.salesTrendDays,
        }),
        this.$store.dispatch("getInvoices", {
          currentPage: 1,
          resultsPerPage: 5,
        }),
      ]);
    },
    refreshCategoryReport() {
      this.$store.dispatch("getSalesByCategoryReport", { trend: "month" });
    },
    formatCurrency(value) {
      const numberValue = Number(value || 0);
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numberValue);
    },
    formatNumber(value) {
      return new Intl.NumberFormat().format(Number(value || 0));
    },
    formatDate(value) {
      if (!value) {
        return "--";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
    formatDateLabel(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return "";
      }
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    },
    toNumber(value) {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : 0;
    },
    updateTopProductsChart(data) {
      if (!data || data.length === 0) {
        this.topProductsChartData = [];
        return;
      }
      this.topProductsChartData = data.map((item, index) => {
        const value = this.toNumber(item.quantitySold);
        return {
          label: item.productName,
          value,
          displayValue: `${this.formatNumber(value)} units`,
          color: this.chartPalette[index % this.chartPalette.length],
        };
      });
    },
    updateCategoryChart(data) {
      if (!data || data.length === 0) {
        this.categoryChartData = [];
        return;
      }
      const totalSales = data.reduce(
        (total, item) => total + this.toNumber(item.totalSales),
        0
      );
      const formattedTotal = this.formatCurrency(totalSales);
      this.categoryChartData = data.map((item, index) => {
        const value = this.toNumber(item.totalSales);
        return {
          label: item.category,
          value,
          displayValue: this.formatCurrency(value),
          color: this.chartPalette[index % this.chartPalette.length],
          displayTotal: index === 0 ? formattedTotal : "",
        };
      });
      if (this.categoryChartData.length > 0) {
        this.categoryChartData[0].displayTotal = formattedTotal;
      }
    },
    updateSalesTrendChart(data) {
      if (!data || data.length === 0) {
        this.salesTrendChartData = [];
        this.salesSummary = { totalRevenue: 0, totalOrders: 0 };
        return;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = new Date(today);
      start.setDate(today.getDate() - (this.salesTrendDays - 1));
      const dateMap = new Map();
      data.forEach((entry) => {
        if (!entry.date) {
          return;
        }
        const dateKey = new Date(entry.date);
        if (Number.isNaN(dateKey.getTime())) {
          return;
        }
        dateKey.setHours(0, 0, 0, 0);
        const isoKey = dateKey.toISOString().slice(0, 10);
        dateMap.set(isoKey, {
          revenue: this.toNumber(entry.totalRevenue),
          orders: this.toNumber(entry.totalOrders),
        });
      });
      const labels = [];
      const revenueSeries = [];
      const orderSeries = [];
      for (
        let cursor = new Date(start);
        cursor <= today;
        cursor.setDate(cursor.getDate() + 1)
      ) {
        const isoKey = cursor.toISOString().slice(0, 10);
        const metrics = dateMap.get(isoKey) || { revenue: 0, orders: 0 };
        labels.push(new Date(cursor));
        revenueSeries.push(metrics.revenue);
        orderSeries.push(metrics.orders);
      }
      this.salesTrendChartData = labels.map((date, index) => ({
        label: this.formatDateLabel(date),
        revenue: revenueSeries[index],
        orders: orderSeries[index],
      }));
      this.salesSummary = {
        totalRevenue: revenueSeries.reduce(
          (total, amount) => total + amount,
          0
        ),
        totalOrders: orderSeries.reduce((total, amount) => total + amount, 0),
      };
    },
    statusBadge(status) {
      if (!status) {
        return "badge bg-secondary";
      }
      const normalized = status.toString().toLowerCase();
      if (normalized === "paid") {
        return "badge bg-success";
      }
      if (normalized === "partial" || normalized === "processing") {
        return "badge bg-warning text-dark";
      }
      return "badge bg-secondary";
    },
    formatStatus(status) {
      if (!status) {
        return "Unknown";
      }
      return status
        .toString()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
};
</script>

<style scoped>
.summary-card .card-body {
  padding: 1.5rem 1.5rem 1.25rem;
}

.summary-card h3 {
  font-weight: 600;
  font-size: 1.9rem;
}

.table-responsive {
  max-height: 360px;
}

.table-responsive table {
  min-width: 100%;
}

.badge {
  font-weight: 600;
  letter-spacing: 0.02em;
}
</style>
